// Angular
import {Injectable, Injector} from '@angular/core';

// RXJS && NGRX
import {Observable} from 'rxjs';
import {take} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';

import {
  constants,
  analyticsConstants
} from '../../constants';
import * as lodash from 'lodash';
import * as fromRoot from '../../reducers';
import {TelemetryGenericEnum} from '../../shared/enums';
import {ObjectManagerUtils} from '../../utils/object-manager.utils';
import {GuestService} from '../../modules/core/services/guest.service';
import {BookingService} from '../../modules/core/services/booking.service';
import {GuestDetailsService} from '../../modules/core/services/guest-details.service';
import {ImmigrationInformation} from '../../models/guest-details/immigration-information';

@Injectable({
  providedIn: 'root'
})
export class TrackingUtilsService {
  resourceBundle$: Observable<any>;

  private guestService: GuestService;
  private bookingService: BookingService;
  private objectManagerUtils: ObjectManagerUtils;
  private guestDetailsService: GuestDetailsService;

  constructor(
    private readonly store: Store<fromRoot.AppState>,
    private readonly injector : Injector,
  ) {
    this.injectServices();
  }

  injectServices() {
    this.guestDetailsService = this.injector.get<GuestDetailsService>(GuestDetailsService);
    this.bookingService = this.injector.get<BookingService>(BookingService);
    this.guestService = this.injector.get<GuestService>(GuestService);
    this.objectManagerUtils = this.injector.get<ObjectManagerUtils>(ObjectManagerUtils);
  }

  get countryOfResidence(): string {
    let value = '';
    this.guestDetailsService.immigrationInfo
      .subscribe(
        (immigrationInformation: ImmigrationInformation) => {
          value = immigrationInformation?.countryOfResidence ?? TelemetryGenericEnum.EMPTY_VALUE;
        });
    return value;
  }

  getGuestData(): any {
    let result;
    this.store
      .pipe(select(fromRoot.getSessionVar), take(1)) // getReservationRetrievalResp in session.
      .subscribe(res => {
        result = res;
      });
    return result;
  }

  getSelectedGuestId(): string {
    let result = '';
    this.store.pipe(select(fromRoot.getEntitySelectedOther), take(1)).subscribe(res => {
      result = res?.guestId ?? constants.DEFAULT_GUEST_ID_FOR_TRACKING.ID;
    });
    return result;
  }

  getGuestList(): any {
    let result;
    this.store.pipe(select(fromRoot.getEntitySGuests), take(1)).subscribe(res => {
      result = res;
    });
    return result;
  }

  getCompletedGuestList(): any {
    let result;
    this.store.pipe(select(fromRoot.getGuestListCompleted), take(1)).subscribe(res => {
      result = res;
    });
    return result;
  }

  getVdsId() {
    let result;
    this.store.pipe(select(fromRoot.getVdsId), take(1)).subscribe(res => {
      result = res;
    });
    return result;
  }

  getContentRetrieval(): any {
    let result;
    this.store.pipe(select(fromRoot.getContentRetrieval), take(1)).subscribe(res => {
      result = res;
    });
    return result;
  }

  getCurrentLanguageCode(): any {
    let result;
    this.store.pipe(select(fromRoot.getLanguageCode), take(1)).subscribe(res => {
      result = res;
    });
    return result;
  }

  getSetSailPass(): any {
    let result;
    this.store.pipe(select(fromRoot.getSetSailPass), take(1)).subscribe(res => {
      result = res ?? '';
    });
    return result;
  }

  getGuestsSelectedForCheckin(): any {
    return this.getGuestList().filter(
      (guest: any) =>
        guest.selectedForCheckin === true &&
        (guest.olciStatus !== constants.OLCI_STATUS.COMPLETED || guest.olciStatus === constants.OLCI_STATUS.REPRINT)
    );
  }

  getBookingType(): any {
    const bookingType = this.getGuestData()?.bookingType ?? TelemetryGenericEnum.EMPTY_VALUE;
    return this.objectManagerUtils.accessObjectByIndex(
      analyticsConstants.BOOKING_TYPES.CONSTANTS,
      bookingType,
      analyticsConstants.BOOKING_TYPES.DEFAULT
    );
  }

  getBrandCode(): string {
    const brandCode = this.getGuestData()?.brandCode ?? TelemetryGenericEnum.EMPTY_VALUE;
    return this.objectManagerUtils.accessObjectByIndex(
      analyticsConstants.BRAND_TYPES.CONSTANTS,
      brandCode,
      analyticsConstants.BRAND_TYPES.DEFAULT
    );
  }

  getSelectedGuest(): number {
    return this.guestDetailsService.snapshot.selectedGuestId;
  }

  getTrackingGuestStatus(): string {
    return this.guestService.trackingGuestStatus(this.getSelectedGuest());
  }

  getLoyaltyNum(guestId?: any): any {
    const selectedGuest = this.guestDetailsService.getGuestDetails(guestId);
    return lodash.get(selectedGuest, 'personalInfo.crownAndAnchorNumber', '');
  }

  getPlatform(): string {
    return  /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent) ? constants.PLATFORMS.MOBILE : constants.PLATFORMS.WEB
  }

  getSimplifiedShipName(): any {
    const rd = this.bookingService.reservationData;
    return rd ? rd.shipCode : '';
  }

  getLoggedInBookingId(): any {
    return this.getGuestData()?.bookingId ?? '';
  }

  getListOfAdults(): any {
    return this.getGuestList().filter((item: any) => item.minor === false);
  }

  getListOfMinors(): any {
    return this.getGuestList().filter((item: any) => item.minor === true);
  }

  getDateNoDashes(): any {
    const guestData = this.getGuestData();
    return guestData?.departureDate ? guestData.departureDate.replace(/-/g, '') : TelemetryGenericEnum.EMPTY_VALUE;
  }

  getSspBarCode(): any {
    const ssp = this.getSetSailPass();
    return ssp && ssp.barcode ? ssp.barcode : '';
  }

  getSspStateRoom(): any {
    const ssp = this.getSetSailPass();
    return ssp && ssp.stateRoom ? ssp.stateRoom : '';
  }

  // reservation guests
  getReservationAdults(resnumber: any): any {
    const adultsList = this.getListOfAdults();
    return adultsList.filter((item: any) => item.bookingId === resnumber);
  }

  getReservationMinors(resnumber: any): any {
    const minorsList = this.getListOfMinors();
    return minorsList.filter((item: any) => item.bookingId === resnumber);
  }

  getReservationGuests(resnumber: any): any {
    const guestList = this.getGuestList();
    return guestList.filter((item: any) => item.bookingId === resnumber);
  }

  // associated guests
  getAssociatedAdults(resnumber: any): any {
    const associatedAdults = this.getListOfAdults();
    return associatedAdults.filter((item: any) => item.bookingId !== resnumber);
  }

  getAssociatedMinors(resnumber: any): any {
    const associatedMinors = this.getListOfMinors();
    return associatedMinors.filter((item: any) => item.bookingId !== resnumber);
  }

  getAssociatedGuests(resnumber: any): any {
    const associatedGuests = this.getGuestList();
    return associatedGuests.filter((item: any) => item.bookingId !== resnumber);
  }

  getNumberOfAssociatedReservations(resnumber: any): any {
    const associatedReservations = this.getAssociatedGuests(resnumber);
    const resArray = associatedReservations.map((item: any) => item.bookingId);
    return lodash.uniq(resArray);
  }

  // checking in
  getAssociatedAdultsCheckingIn(): any {
    const checkingIn = this.getGuestsSelectedForCheckin();
    const sessionBookingId = this.getLoggedInBookingId();
    const associatedGuests = checkingIn.filter((item: any) => item.bookingId !== sessionBookingId);
    return associatedGuests.filter((item: any) => item.minor === false);
  }

  getAssociatedMinorsCheckingIn(): any {
    const checkingIn = this.getGuestsSelectedForCheckin();
    const sessionBookingId = this.getLoggedInBookingId();
    const associatedGuests = checkingIn.filter((item: any) => item.bookingId !== sessionBookingId);
    return associatedGuests.filter((item: any) => item.minor === true);
  }

  getAssociatedGuestsCheckingIn(): any {
    const checkingIn = this.getGuestsSelectedForCheckin();
    const sessionBookingId = this.getLoggedInBookingId();
    return checkingIn.filter((item: any) => item.bookingId !== sessionBookingId);
  }

  getReservationAdultsCheckingIn(): any {
    const checkingIn = this.getGuestsSelectedForCheckin();
    const sessionBookingId = this.getLoggedInBookingId();
    const guestsList = checkingIn.filter((item: any) => item.bookingId === sessionBookingId);
    return guestsList.filter((item: any) => item.minor === false);
  }

  getReservationMinorsCheckingIn(): any {
    const checkingIn = this.getGuestsSelectedForCheckin();
    const sessionBookingId = this.getLoggedInBookingId();
    const guestsList = checkingIn.filter((item: any) => item.bookingId === sessionBookingId);
    return guestsList.filter((item: any) => item.minor === true);
  }

  getReservationGuestsCheckingIn(): any {
    const checkingIn = this.getGuestsSelectedForCheckin();
    const sessionBookingId = this.getLoggedInBookingId();
    return checkingIn.filter((item: any) => item.bookingId === sessionBookingId);
  }

  getTotalAdultsCheckingIn(): any {
    const checkingIn = this.getGuestsSelectedForCheckin();
    return checkingIn.filter((item: any) => item.minor === false);
  }

  getTotalMinorsCheckingIn(): any {
    const checkingIn = this.getGuestsSelectedForCheckin();
    return checkingIn.filter((item: any) => item.minor === true);
  }

  // checked in
  getAssociatedAdultsCheckedIn(): any {
    const guestsCheckedIn = this.getCompletedGuestList();
    const sessionBookingId = this.getLoggedInBookingId();
    const associatedGuests = guestsCheckedIn.filter((item: any) => item.bookingId !== sessionBookingId);
    return associatedGuests.filter((item: any) => item.minor === false);
  }

  getAssociatedMinorsCheckedIn(): any {
    const guestsCheckedIn = this.getCompletedGuestList();
    const sessionBookingId = this.getLoggedInBookingId();
    const associatedGuests = guestsCheckedIn.filter((item: any) => item.bookingId !== sessionBookingId);
    return associatedGuests.filter((item: any) => item.minor === true);
  }

  getAssociatedGuestsCheckedIn(): any {
    const guestsCheckedIn = this.getCompletedGuestList();
    const sessionBookingId = this.getLoggedInBookingId();
    return guestsCheckedIn.filter((item: any) => item.bookingId !== sessionBookingId);
  }

  getReservationAdultsCheckedIn(): any {
    const guestsCheckedIn = this.getCompletedGuestList();
    const sessionBookingId = this.getLoggedInBookingId();
    const guestsList = guestsCheckedIn.filter((item: any) => item.bookingId === sessionBookingId);
    return guestsList.filter((item: any) => item.minor === false);
  }

  getReservationMinorsCheckedIn(): any {
    const guestsCheckedIn = this.getCompletedGuestList();
    const sessionBookingId = this.getLoggedInBookingId();
    const guestsList = guestsCheckedIn.filter((item: any) => item.bookingId === sessionBookingId);
    return guestsList.filter((item: any) => item.minor === true);
  }

  getReservationGuestsCheckedIn(): any {
    const guestsCheckedIn = this.getCompletedGuestList();
    const sessionBookingId = this.getLoggedInBookingId();
    return guestsCheckedIn.filter((item: any) => item.bookingId === sessionBookingId);
  }

  getTotalAdultsCheckedIn(): any {
    const guestsCheckedIn = this.getCompletedGuestList();
    return guestsCheckedIn.filter((item: any) => item.minor === false);
  }

  getTotalMinorsCheckedIn(): any {
    const guestsCheckedIn = this.getCompletedGuestList();
    return guestsCheckedIn.filter((item: any) => item.minor === true);
  }

  retrieveValueApplyingNullCheckValidation(returnNullCheckParam: any, value?: any) {
    return value ?? returnNullCheckParam;
  }
}
